import Help from '@mui/icons-material/Help';
import Remove from '@mui/icons-material/Remove';
import TrendingDown from '@mui/icons-material/TrendingDown';
import TrendingUp from '@mui/icons-material/TrendingUp';
import { Skeleton, Theme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { MetricPanelProps, MetricsField } from './MetricTypes';

const useStyles = makeStyles((theme: Theme) => ({
  metricPanelContainerOutline: {
    width: '22.5%',
    // maxWidth: '360px',
    minWidth: '260px',
    height: '88px',
    border: '1px solid rgba(240, 240, 240, .2)',
    boxShadow:
      '0 0 0 0.05rem rgb(8 60 130 / 10%), 0rem 0rem 1.25rem rgb(30 34 40 / 4%)',
    '.hover &': {
      border: '1.5px solid #0098C7',
      boxShadow:
        '0 0 0 0.05rem rgb(0 152 199/ 10%), 0rem 0rem 1.25rem rgb(0 152 199 / 4%)',
    },
    '&:hover': {
      cursor: 'initial',
    },
  },
  metricPanelContainerBox: {
    boxShadow:
      '0 0 0 0.05rem rgb(8 60 130 / 10%), 0rem 0rem 1.25rem rgb(30 34 40 / 4%)',
    borderRadius: '5px',
    padding: '12px 4px 4px 12px',
    margin: '16px 8px',
    cursor: 'pointer',
  },
  metricPanelTopRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  metricPanelTitle: {
    fontSize: '12px',
    fontWeight: 500,
    textAlign: 'start',
    marginBottom: '0px',
  },
  metricPanelBottomRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metricPanelBottomRowLeftColumnContainer: {},
  metricPanelBottomRowRightColumnContainer: {},
  metricPanelData: {
    fontWeight: 400,
    fontSize: '40px',
    maxWidth: '110px',
    marginBottom: '0px',
  },
  metricPanelUpwardArrow: {
    height: '36px',
    width: '36px',
    color: 'rgb(63,200,183)',
  },
  metricPanelDownwardArrow: {
    height: '36px',
    width: '36px',
    color: 'rgb(251,91,93)',
  },
  metricPanelHorizontalArrow: {
    height: '40px',
    width: '40px',
  },
  metricPanelDeltaContainerOutline: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '12px',
  },
  metricPanelArrowContainerOutline: {},
  metricPanelArrowIconContainerOutline: {
    width: '40px',
    display: 'flex',
    margin: '0px 4px',
    justifyContent: 'center',
  },
  metricPanelPercentageChangeContainerOutline: {},
  metricPanelPercentageChangeSubContainerOutline: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  metricPanelPercentageChange: {
    fontWeight: 400,
    fontSize: '16px',
    marginBottom: '0px',
    maxWidth: '64px',
    lineHeight: '22px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  metricPanelText: {
    fontSize: '10px',
    fontWeight: 300,
    minWidth: '47px',
    lineHeight: '8px',
    marginTop: '4px',
    marginBottom: '0px',
    wrap: 'no wrap',
  },
  questionMarkIcon: {
    borderRadius: '50%',
    height: '16px',
    width: '16px',
    padding: '2px',
  },
  metricPanelSkeletonContainerOutline: {
    width: '22%',
    maxWidth: '268px',
    minWidth: '220px',
    height: '96px',
  },
  metricPanelSkeletonContainerBox: {
    padding: '0px',
    margin: '16px',
  },
  skeleton: {
    backgroundColor: theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey',
    height: '6em',
    margin: 10,
  },
}));

const getFontSizePercentage = (data: number | string) => {
  if (!data) {
    return 16;
  }
  const dataStr = String(data);
  if (dataStr?.length < '100000'.length) {
    return 16;
  } else if (dataStr?.length < '1000000'.length) {
    return 14;
  } else if (dataStr?.length < '10000000'.length) {
    return 12;
  } else {
    return 10;
  }
};

const getFontSize = (
  data: number | string,
  metricField: MetricsField | undefined
) => {
  if (metricField?.dataFontSize) {
    return metricField.dataFontSize;
  }
  if (!data) {
    return 40;
  }
  const dataStr = String(data);
  if (dataStr?.length < '10000'.length) {
    return 40;
  } else if (dataStr?.length < '100000'.length) {
    return 36;
  } else if (dataStr?.length < '1000000'.length) {
    return 32;
  } else {
    return 28;
  }
};

const TrendingArrow = (props: { percentage: number }) => {
  const classes = useStyles();
  return (
    <div className={classes.metricPanelArrowIconContainerOutline}>
      {props.percentage > 0 ? (
        <TrendingUp className={classes.metricPanelUpwardArrow} />
      ) : props.percentage < 0 ? (
        <TrendingDown className={classes.metricPanelDownwardArrow} />
      ) : (
        <Remove className={classes.metricPanelHorizontalArrow} />
      )}
    </div>
  );
};

export const MetricPanelDelta = (props: {
  percentage: number | string;
  previous?: string;
}) => {
  const { percentage } = props;
  const classes = useStyles();
  const roundedPercentage =
    Math.floor(Number(percentage)) === Number(percentage)
      ? Number(percentage)
      : Number(percentage)?.toFixed(1) ?? '';
  if (roundedPercentage === '') {
    return <></>;
  }
  const percentageFontSize = getFontSizePercentage(roundedPercentage);
  return (
    <div className={classes.metricPanelDeltaContainerOutline}>
      <div className={classes.metricPanelArrowContainerOutline}>
        <TrendingArrow percentage={Number(props.percentage)} />
      </div>
      <div className={classes.metricPanelPercentageChangeContainerOutline}>
        <div className={classes.metricPanelPercentageChangeSubContainerOutline}>
          <Tooltip title={`${roundedPercentage}%`}>
            <p
              style={{
                fontSize: percentageFontSize,
                lineHeight: `${42 - percentageFontSize}px`,
              }}
              className={classes.metricPanelPercentageChange}
            >{`${roundedPercentage}%`}</p>
          </Tooltip>
        </div>
        <div className={classes.metricPanelPercentageChangeSubContainerOutline}>
          <p className={classes.metricPanelText}>from prev</p>
          <Tooltip title={props.previous ?? ''} placement="top-start" arrow>
            <Help className={classes.questionMarkIcon} />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const MetricPanel = (props: MetricPanelProps) => {
  const classes = useStyles();

  const { title, data, percentage, previous, loading } = props;

  const roundedData =
    typeof data === 'string'
      ? data
      : Math.floor(Number(data)) === Number(data)
      ? Number(data)
      : data ?? '';

  const dataFontSize = getFontSize(roundedData, props.metricField);

  if (loading) {
    return (
      <Paper
        className={clsx(
          classes.metricPanelSkeletonContainerOutline,
          classes.metricPanelSkeletonContainerBox
        )}
        elevation={0}
      >
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
        <Skeleton
          className={classes.skeleton}
          variant="rectangular"
          height={10}
        />
      </Paper>
    );
  }

  return (
    <div
      className={clsx(
        classes.metricPanelContainerBox,
        classes.metricPanelContainerOutline
      )}
    >
      <div className={classes.metricPanelTopRow}>
        <p className={classes.metricPanelTitle}>{title}</p>
      </div>
      <div className={classes.metricPanelBottomRow}>
        <div className={classes.metricPanelBottomRowLeftColumnContainer}>
          <Tooltip title={data ?? ''} placement="top-start" arrow>
            <p
              style={{
                fontSize: dataFontSize,
                lineHeight: `${92 - dataFontSize}px`,
              }}
              className={classes.metricPanelData}
            >
              {typeof roundedData === 'number' &&
              props?.title?.includes('%') ? (
                <span>{Math.round(roundedData * 100) / 100}%</span>
              ) : (
                <span>{roundedData}</span>
              )}
            </p>
          </Tooltip>
        </div>
        <div className={classes.metricPanelBottomRowRightColumnContainer}>
          <MetricPanelDelta percentage={percentage} previous={previous} />
        </div>
      </div>
    </div>
  );
};
export default MetricPanel;
